import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import { CreditCard } from '@mui/icons-material';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { apiService } from '../services/api';

// Initialize Stripe with publishable key (skip during react-snap prerender)
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const isPrerender =
  typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent || '');

if (!isPrerender && (!STRIPE_KEY || STRIPE_KEY === '')) {
  console.error('Missing REACT_APP_STRIPE_PUBLISHABLE_KEY in environment variables');
}

const stripePromise =
  !isPrerender && STRIPE_KEY ? loadStripe(STRIPE_KEY) : Promise.resolve(null);

interface PaymentMethodSetupProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  bidId?: string;
  customerName?: string;
  amount?: number;
  existingCustomerId?: string; // NEW: For property-specific payment setup
}

// Payment form component that uses Stripe Elements
const PaymentForm: React.FC<{
  onSuccess: () => void;
  onError: (error: string) => void;
  onClose: () => void;
  bidId?: string;
  existingCustomerId?: string; // NEW: For property-specific payment setup
}> = ({ onSuccess, onError, onClose, bidId, existingCustomerId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    try {
      // Create a Setup Intent via your backend
      // Pass existingCustomerId for property-specific payment setup
      const response = await apiService.post<any>('/payments/create-setup-intent', { 
        bidId,
        existingCustomerId, // NEW: Link payment method to specific property
      });
      
      console.log('Setup Intent API Response:', response, {
        bidId,
        existingCustomerId,
        note: existingCustomerId ? 'Property-specific payment setup' : 'Customer-level payment setup',
      });
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to create setup intent');
      }

      const clientSecret = (response as any).clientSecret || response.data?.clientSecret;
      
      if (!clientSecret) {
        console.error('No clientSecret in response:', response);
        throw new Error('No client secret returned from server');
      }
      
      console.log('Client Secret received:', clientSecret.substring(0, 20) + '...');

      // Confirm the setup with the payment method
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (confirmError) {
        console.error('Stripe confirmCardSetup error:', confirmError);
        throw confirmError;
      }

      if (setupIntent && setupIntent.status === 'succeeded') {
        // Save the payment method to the backend
        // Pass existingCustomerId to link payment method to specific property
        const saveResponse = await apiService.post('/payments/save-payment-method', {
          setupIntentId: setupIntent.id,
          bidId,
          existingCustomerId, // NEW: Link payment method to specific property
        });
        
        console.log('Save payment method response:', saveResponse, {
          existingCustomerId,
          note: existingCustomerId ? 'Payment method linked to property' : 'Payment method at customer level',
        });
        
        if (!saveResponse.success) {
          throw new Error(saveResponse.error || 'Failed to save payment method');
        }

        onSuccess();
      }
    } catch (err: any) {
      onError(err.message || 'Failed to save payment method');
    } finally {
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Card Information
        </Typography>
        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <CardElement options={cardElementOptions} />
        </Box>
      </Box>

      <Box display="flex" gap={2} justifyContent="flex-end">
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || loading}
        >
          {loading ? 'Saving...' : 'Save Payment Method'}
        </Button>
      </Box>
    </form>
  );
};

const PaymentMethodSetup: React.FC<PaymentMethodSetupProps> = ({
  open,
  onClose,
  onSuccess,
  bidId,
  customerName,
  amount,
  existingCustomerId, // NEW: For property-specific payment setup
}) => {
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Box textAlign="center" mb={3}>
            <CreditCard color="primary" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Payment Method Setup
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {existingCustomerId 
                ? 'Save your payment method for this property. No charges will be made today.'
                : 'Save your payment method securely. No charges will be made today.'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Elements stripe={stripePromise}>
            <PaymentForm
              onSuccess={onSuccess}
              onError={setError}
              onClose={onClose}
              bidId={bidId}
              existingCustomerId={existingCustomerId} // NEW: Pass to PaymentForm
            />
          </Elements>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentMethodSetup;